// Tailwind UI Components - Nahrazení Bootstrap JS funkcionaliy
import { icon } from './icons.js';

// Modal funkcionality
class TailwindModal {
  constructor(modalId) {
    this.modal = document.getElementById(modalId);
    this.backdrop = this.modal?.querySelector('#modal-backdrop');
    this.closeBtn = this.modal?.querySelector('#modal-close-btn');
    this.cancelBtn = this.modal?.querySelector('#modal-cancel-btn');
    
    this.init();
  }
  
  init() {
    if (!this.modal) return;
    
    // Event listenery pro zavírání modalu
    this.closeBtn?.addEventListener('click', () => this.hide());
    this.cancelBtn?.addEventListener('click', () => this.hide());
    this.backdrop?.addEventListener('click', () => this.hide());
    
    // Zavřít modal při ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.classList.contains('hidden')) {
        this.hide();
      }
    });
  }
  
  show() {
    if (!this.modal) return;
    
    this.modal.classList.remove('hidden');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Focus na první input
    const firstInput = this.modal.querySelector('input, textarea, select');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
  }
  
  hide() {
    if (!this.modal) return;
    
    this.modal.classList.add('hidden');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Vyčisti formulář
    const form = this.modal.querySelector('form');
    if (form) {
      form.reset();
    }
    
    // Skryj error zprávy
    const errorDiv = this.modal.querySelector('[id$="-error"]');
    if (errorDiv) {
      errorDiv.classList.add('hidden');
    }
  }
  
  static getInstance(modalId) {
    return new TailwindModal(modalId);
  }
}

// Tab funkcionalita
class TailwindTabs {
  constructor(tabContainer) {
    this.container = typeof tabContainer === 'string' 
      ? document.querySelector(tabContainer) 
      : tabContainer;
    
    if (!this.container) return;
    
    this.tabs = this.container.querySelectorAll('[data-tab]');
    this.tabPanes = document.querySelectorAll('.tab-pane');
    
    this.init();
  }
  
  init() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(tab.getAttribute('data-tab'));
      });
    });
  }
  
  switchTab(targetTab) {
    // Deaktivuj všechny taby
    this.tabs.forEach(tab => {
      tab.classList.remove('border-blue-500', 'text-blue-600');
      tab.classList.add('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300');
    });
    
    // Aktivuj kliknutý tab
    const activeTab = document.querySelector(`[data-tab="${targetTab}"]`);
    if (activeTab) {
      activeTab.classList.add('border-blue-500', 'text-blue-600');
      activeTab.classList.remove('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300');
    }
    
    // Skryj všechny tab panes
    this.tabPanes.forEach(pane => {
      pane.classList.remove('active');
    });
    
    // Zobraz aktivní tab pane
    const activePane = document.getElementById(targetTab);
    if (activePane) {
      activePane.classList.add('active');
    }

    // Na mobilu (< sm): po přepnutí tabu odscrolluj na začátek formuláře.
    // Předchozí pane mohl být dlouhý a uživatel odscrolovaný hluboko – nový
    // (často kratší) pane by jinak zůstal mimo dohled.
    if (window.matchMedia('(max-width: 639.98px)').matches) {
      const header = this.container.closest('.border-b');
      if (header) header.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// Mobile menu funkcionalita – levý drawer s backdropem
class MobileMenu {
  constructor() {
    this.menuBtn = document.getElementById('mobile-menu-btn');
    this.mobileMenu = document.getElementById('mobile-menu');
    this.backdrop = document.getElementById('mobile-menu-backdrop');

    this.init();
  }

  init() {
    if (!this.menuBtn || !this.mobileMenu) return;

    this.menuBtn.addEventListener('click', () => {
      this.toggle();
    });

    // Zavři menu při kliknutí na link
    const menuLinks = this.mobileMenu.querySelectorAll('a[data-section]');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.hide();
      });
    });

    // Zavři menu při kliknutí na backdrop
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.hide());
    }

    // Zavři menu na Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) this.hide();
    });
  }

  toggle() {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible() {
    return this.mobileMenu.classList.contains('opacity-100');
  }

  hide() {
    this.mobileMenu.classList.remove('opacity-100');
    this.mobileMenu.classList.add('opacity-0');
    if (this.backdrop) {
      this.backdrop.classList.remove('is-visible');
      this.backdrop.classList.add('hidden');
    }
    this.menuBtn.setAttribute('aria-expanded', 'false');
  }

  show() {
    this.mobileMenu.classList.remove('opacity-0');
    this.mobileMenu.classList.add('opacity-100');
    if (this.backdrop) {
      this.backdrop.classList.remove('hidden');
      // force reflow pro přechod opacity z 0 → 1
      void this.backdrop.offsetWidth;
      this.backdrop.classList.add('is-visible');
    }
    this.menuBtn.setAttribute('aria-expanded', 'true');
  }
}

// Notification systém
function showTailwindNotification(message, type = 'info') {
  // Odstranění starých notifikací
  const existingNotifications = document.querySelectorAll('.notification');
  existingNotifications.forEach(n => n.remove());
  
  const notification = document.createElement('div');
  notification.className = `notification fixed top-4 right-4 z-50 max-w-sm p-4 rounded-md shadow-lg transform transition-all duration-300 translate-x-full`;
  
  const colors = {
    'success': 'bg-green-50 border border-green-200 text-green-800',
    'error': 'bg-red-50 border border-red-200 text-red-800',
    'warning': 'bg-yellow-50 border border-yellow-200 text-yellow-800',
    'info': 'bg-blue-50 border border-blue-200 text-blue-800'
  };
  
  notification.className += ` ${colors[type] || colors.info}`;
  
  notification.innerHTML = `
    <div class="flex">
      <div class="flex-1">
        <p class="text-sm font-medium">${message}</p>
      </div>
      <div class="ml-4 flex-shrink-0 flex">
        <button class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none">
          ${icon('x-mark', 'h-5 w-5')}
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  // Animace příchodu
  setTimeout(() => {
    notification.classList.remove('translate-x-full');
  }, 10);
  
  // Auto-hide po 5 sekundách
  setTimeout(() => {
    notification.classList.add('translate-x-full');
    setTimeout(() => notification.remove(), 300);
  }, 5000);
  
  // Zavírací tlačítko
  const closeBtn = notification.querySelector('button');
  closeBtn.addEventListener('click', () => {
    notification.classList.add('translate-x-full');
    setTimeout(() => notification.remove(), 300);
  });
}

// Inicializace po načtení DOM
document.addEventListener('DOMContentLoaded', function() {
  // Inicializuj modal
  const loginModal = new TailwindModal('loginModal');
  window.TailwindModal = TailwindModal; // Global přístup
  
  // Inicializuj taby
  const tabContainer = document.querySelector('nav[role="tablist"]');
  if (tabContainer) {
    new TailwindTabs(tabContainer);
  }
  
  // Inicializuj mobile menu
  window.mobileMenu = new MobileMenu();
  
  // Global funkce pro notifikace
  window.showNotification = showTailwindNotification;
  
  console.log('Tailwind UI components initialized');
}); 